import React, { useState, useEffect, useRef } from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	CircularProgress,
	ClickAwayListener,
	Grid,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	Paper,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import VisitorPassModal from "./VisitorPassModal";
import {
	Control,
	FieldErrors,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from "react-hook-form";
import PhoneInputField from "src/components/Common/PhoneInputField";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import { useDebounce } from "src/hooks/useDebounce";
import type { IVisitorListItem } from "../type/newVisitorType";

export interface VisitorFormValues {
	visitor_id: number | null;
	visitor_phone: string;
	visitor_name: string;
	visitor_email: string;
	password?: string;
	date: string;
	quantity: number;
	discount: number;
	payment_method: string;
	payment_status: string;
}

interface Props {
	control: Control<VisitorFormValues>;
	register: UseFormRegister<VisitorFormValues>;
	errors: FieldErrors<VisitorFormValues>;
	setValue: UseFormSetValue<VisitorFormValues>;
	watch: UseFormWatch<VisitorFormValues>;
	selectedVisitor: IVisitorListItem | null;
	setSelectedVisitor: (visitor: IVisitorListItem | null) => void;
}

const VisitorPhoneSearchSection: React.FC<Props> = ({
	control,
	register,
	errors,
	setValue,
	watch,
	selectedVisitor,
	setSelectedVisitor,
}) => {
	const theme = useTheme();
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [openSuggestions, setOpenSuggestions] = useState<boolean>(false);
	const [passModalOpen, setPassModalOpen] = useState<boolean>(false);

	const watchedPhone = watch("visitor_phone") || "";
	const debouncedPhone = useDebounce(watchedPhone, 400);

	// Flag to avoid re-opening suggestions right after user clicks an item
	const justSelectedRef = useRef<boolean>(false);

	// Strip non-digits for robust phone comparison
	const cleanPhoneDigits = (debouncedPhone || "").replace(/\D/g, "");
	const isPhoneLongEnough = cleanPhoneDigits.length >= 6;

	// Search visitor query by phone number
	const { data: searchResultData, isFetching: isSearching } =
		useGetQueryWithTotal<IVisitorListItem[]>({
			endPoint: "/api/v1/admin/visitor",
			queryKeys: ["SearchVisitorByPhone", debouncedPhone],
			params: {
				limit: 10,
				skip: 0,
				searchQuery: debouncedPhone?.trim() || "",
			},
			enabled: Boolean(isPhoneLongEnough),
		});

	// Keep track of the last auto-populated visitor id
	const lastMatchedVisitorIdRef = useRef<number | null>(null);

	// Sync ref if parent resets selectedVisitor
	useEffect(() => {
		if (!selectedVisitor) {
			lastMatchedVisitorIdRef.current = null;
		}
	}, [selectedVisitor]);

	// Auto-select ONLY when there is a 100% full exact phone number match
	useEffect(() => {
		if (!isPhoneLongEnough) {
			if (lastMatchedVisitorIdRef.current || selectedVisitor) {
				setSelectedVisitor(null);
				lastMatchedVisitorIdRef.current = null;
				setValue("visitor_id", null);
				setValue("visitor_name", "");
				setValue("visitor_email", "");
			}
			setOpenSuggestions(false);
			return;
		}

		if (justSelectedRef.current) {
			justSelectedRef.current = false;
			setOpenSuggestions(false);
			return;
		}

		const visitorsList = searchResultData?.data || [];

		// Match visitor ONLY on full complete phone number (10+ digits), never on loose partial typing!
		const matchedVisitor = visitorsList.find((v: IVisitorListItem) => {
			if (!v.visitor_phone || cleanPhoneDigits.length < 10) return false;
			const vDigits = v.visitor_phone.replace(/\D/g, "");
			if (v.visitor_phone.trim() === debouncedPhone.trim()) return true;
			if (vDigits === cleanPhoneDigits) return true;
			if (cleanPhoneDigits.length === 11 && vDigits === `88${cleanPhoneDigits}`) return true;
			if (cleanPhoneDigits.length === 13 && cleanPhoneDigits.startsWith("880") && vDigits === cleanPhoneDigits.slice(2)) return true;
			return false;
		});

		if (matchedVisitor) {
			if (lastMatchedVisitorIdRef.current !== matchedVisitor.visitor_id) {
				setSelectedVisitor(matchedVisitor);
				lastMatchedVisitorIdRef.current = matchedVisitor.visitor_id;
				setValue("visitor_id", matchedVisitor.visitor_id, { shouldValidate: true });
				setValue("visitor_name", matchedVisitor.visitor_name || "", {
					shouldValidate: true,
				});
				setValue("visitor_email", matchedVisitor.visitor_email || "", {
					shouldValidate: true,
				});
			}
		} else {
			// No exact match -> New Visitor Mode (Fields must remain empty for user input)
			if (lastMatchedVisitorIdRef.current !== null) {
				setSelectedVisitor(null);
				setValue("visitor_id", null);
				lastMatchedVisitorIdRef.current = null;
				setValue("visitor_name", "");
				setValue("visitor_email", "");
				setValue("password", "12345678");
			}
		}
	}, [
		searchResultData,
		debouncedPhone,
		isPhoneLongEnough,
		cleanPhoneDigits,
		setSelectedVisitor,
		setValue,
		selectedVisitor,
	]);

	const handleSelectVisitor = (visitor: IVisitorListItem) => {
		justSelectedRef.current = true;
		setSelectedVisitor(visitor);
		lastMatchedVisitorIdRef.current = visitor.visitor_id;
		setValue("visitor_id", visitor.visitor_id, { shouldValidate: true });
		setValue("visitor_phone", visitor.visitor_phone || "", { shouldValidate: true });
		setValue("visitor_name", visitor.visitor_name || "", { shouldValidate: true });
		setValue("visitor_email", visitor.visitor_email || "", { shouldValidate: true });
		setOpenSuggestions(false);
	};

	const isExisting = Boolean(selectedVisitor);
	const hasEnteredPhone = isPhoneLongEnough;
	const suggestionsList = searchResultData?.data || [];

	return (
		<Paper
			variant="outlined"
			sx={{
				p: 2.5,
				borderRadius: 2,
				bgcolor: isExisting
					? alpha(theme.palette.success.main, 0.03)
					: alpha(theme.palette.primary.main, 0.02),
				borderColor: isExisting
					? alpha(theme.palette.success.main, 0.35)
					: alpha(theme.palette.primary.main, 0.18),
				transition: "all 0.2s ease-in-out",
			}}
		>
			{/* Section Header with Dynamic Status Badge */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 1,
					mb: 2,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<PersonSearchIcon
						sx={{
							color: isExisting ? "success.main" : "primary.main",
							fontSize: 22,
						}}
					/>
					<Typography variant="subtitle1" fontWeight={700} color="text.primary">
						Visitor Information
					</Typography>
				</Box>

				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{/* Status Chip */}
					{isSearching ? (
						<Chip
							icon={<CircularProgress size={13} color="inherit" />}
							label="Searching visitor..."
							size="small"
							variant="outlined"
							sx={{
								fontWeight: 600,
								fontSize: "0.75rem",
								borderColor: "divider",
							}}
						/>
					) : isExisting ? (
						<Chip
							icon={<CheckCircleTwoToneIcon fontSize="small" />}
							label={`Existing Visitor Found ${selectedVisitor?.visitor_no ? `(${selectedVisitor.visitor_no})` : ""
								}`}
							size="small"
							color="success"
							variant="filled"
							sx={{
								fontWeight: 700,
								fontSize: "0.75rem",
								boxShadow: `0 1px 4px ${alpha(theme.palette.success.main, 0.25)}`,
							}}
						/>
					) : hasEnteredPhone ? (
						<Chip
							icon={<HowToRegIcon fontSize="small" />}
							label="New Visitor Registration"
							size="small"
							color="primary"
							variant="outlined"
							sx={{
								fontWeight: 700,
								fontSize: "0.75rem",
								bgcolor: alpha(theme.palette.primary.main, 0.08),
							}}
						/>
					) : (
						<Typography variant="body1" color="text.secondary">
							Enter phone number to search or register
						</Typography>
					)}

					{isExisting && (
						<>
							<Button
								size="small"
								variant="contained"
								color="primary"
								startIcon={<ConfirmationNumberTwoToneIcon fontSize="small" />}
								onClick={() => setPassModalOpen(true)}
								sx={{
									textTransform: "none",
									fontWeight: 700,
									fontSize: "0.75rem",
									py: 0.35,
									px: 1.3,
									borderRadius: 1.5,
									boxShadow: `0 2px 6px ${alpha(
										theme.palette.primary.main,
										0.3
									)}`,
								}}
							>
								Print / Download Pass
							</Button>

							<Button
								size="small"
								color="inherit"
								variant="text"
								onClick={() => {
									justSelectedRef.current = false;
									setSelectedVisitor(null);
									lastMatchedVisitorIdRef.current = null;
									setValue("visitor_id", null);
									setValue("visitor_name", "");
									setValue("visitor_email", "");
									setValue("visitor_phone", "+880");
									setOpenSuggestions(false);
								}}
								sx={{
									fontSize: "0.72rem",
									textTransform: "none",
									py: 0.1,
									px: 0.8,
									color: "text.secondary",
									"&:hover": { color: "primary.main" },
								}}
							>
								Change
							</Button>
						</>
					)}
				</Box>
			</Box>

			<Grid container spacing={2}>
				{/* 1. Phone Number Input with Suggestion Popup Dropdown */}
				<Grid item xs={12}>
					<ClickAwayListener onClickAway={() => setOpenSuggestions(false)}>
						<Box sx={{ position: "relative", width: "100%" }}>
							<PhoneInputField
								name="visitor_phone"
								control={control}
								label="Visitor Phone Number"
								required
								defaultCountry="bd"
								placeholder="Enter phone number (e.g. +8801953096155)"
								error={Boolean(errors.visitor_phone)}
								helperText={
									errors.visitor_phone?.message ||
									(isExisting
										? "✓ Visitor found in records. Details auto-populated."
										: hasEnteredPhone
											? "New phone number. Fill in name & email below to register."
											: "Type visitor's phone number to search or register.")
								}
								onChange={(val) => {
									if (
										selectedVisitor &&
										val !== selectedVisitor.visitor_phone
									) {
										setSelectedVisitor(null);
										lastMatchedVisitorIdRef.current = null;
										setValue("visitor_id", null);
										setValue("visitor_name", "");
										setValue("visitor_email", "");
									}
									setOpenSuggestions(true);
								}}
							/>

							{/* Suggestion Dropdown Popup (Only shown when not already selected and has suggestions) */}
							{openSuggestions &&
								!isExisting &&
								isPhoneLongEnough &&
								suggestionsList.length > 0 && (
									<Paper
										elevation={8}
										sx={{
											position: "absolute",
											top: "calc(100% - 16px)",
											left: 0,
											right: 0,
											zIndex: 1300,
											maxHeight: 280,
											overflowY: "auto",
											borderRadius: 2,
											border: "1px solid",
											borderColor: alpha(theme.palette.primary.main, 0.25),
											boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
											bgcolor: "background.paper",
										}}
									>
										<Box
											sx={{
												px: 2,
												py: 1,
												bgcolor: alpha(theme.palette.primary.main, 0.06),
												borderBottom: "1px solid",
												borderColor: "divider",
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
											}}
										>
											<Typography
												variant="caption"
												fontWeight={700}
												color="primary.main"
												sx={{
													textTransform: "uppercase",
													letterSpacing: 0.5,
												}}
											>
												Matching Visitors ({suggestionsList.length})
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Click to select
											</Typography>
										</Box>

										<List disablePadding>
											{suggestionsList.map((visitor) => {
												const isThisSelected =
													selectedVisitor?.visitor_id ===
													visitor.visitor_id;

												return (
													<ListItemButton
														key={visitor.visitor_id}
														selected={isThisSelected}
														onClick={() =>
															handleSelectVisitor(visitor)
														}
														sx={{
															py: 1.2,
															px: 2,
															borderBottom:
																"1px solid rgba(0, 0, 0, 0.05)",
															display: "flex",
															flexDirection: "column",
															alignItems: "flex-start",
															transition: "all 0.15s ease-in-out",
															"&:hover": {
																bgcolor: alpha(
																	theme.palette.primary.main,
																	0.08
																),
															},
															"&.Mui-selected": {
																bgcolor: alpha(
																	theme.palette.success.main,
																	0.12
																),
																"&:hover": {
																	bgcolor: alpha(
																		theme.palette.success.main,
																		0.18
																	),
																},
															},
														}}
													>
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																justifyContent: "space-between",
																width: "100%",
															}}
														>
															<Typography
																variant="body2"
																sx={{
																	fontWeight: 700,
																	color: "text.primary",
																}}
															>
																{visitor.visitor_name}
															</Typography>
															{visitor.visitor_no && (
																<Typography
																	variant="caption"
																	sx={{
																		bgcolor: isThisSelected
																			? "success.main"
																			: "primary.main",
																		color: "#ffffff",
																		px: 0.8,
																		py: 0.2,
																		borderRadius: 0.75,
																		fontWeight: 700,
																		fontSize: "0.7rem",
																	}}
																>
																	{visitor.visitor_no}
																</Typography>
															)}
														</Box>

														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																gap: 2,
																mt: 0.4,
																flexWrap: "wrap",
															}}
														>
															{visitor.visitor_phone && (
																<Typography
																	variant="caption"
																	sx={{
																		color: "text.secondary",
																		fontWeight: 600,
																		fontSize: "0.78rem",
																		display: "flex",
																		alignItems: "center",
																		gap: 0.3,
																	}}
																>
																	📱 {visitor.visitor_phone}
																</Typography>
															)}
															{visitor.visitor_email && (
																<Typography
																	variant="caption"
																	sx={{
																		color: "text.secondary",
																		fontSize: "0.78rem",
																		display: "flex",
																		alignItems: "center",
																		gap: 0.3,
																	}}
																>
																	✉ {visitor.visitor_email}
																</Typography>
															)}
														</Box>
													</ListItemButton>
												);
											})}
										</List>
									</Paper>
								)}
						</Box>
					</ClickAwayListener>
				</Grid>

				{/* 2. Visitor Name */}
				<Grid item xs={12} sm={isExisting ? 6 : 4}>
					<TextField
						fullWidth
						size="small"
						label="Visitor Name"
						required={!isExisting}
						placeholder="e.g. Jelani Adams"
						{...register("visitor_name", {
							required: !isExisting ? "Visitor name is required" : false,
						})}
						error={Boolean(errors.visitor_name)}
						helperText={errors.visitor_name?.message}
						InputProps={{
							readOnly: isExisting,
							startAdornment: (
								<InputAdornment position="start">
									<PersonIcon
										fontSize="small"
										color={isExisting ? "success" : "action"}
									/>
								</InputAdornment>
							),
						}}
						sx={{
							"& .MuiOutlinedInput-root": {
								bgcolor: isExisting
									? alpha(theme.palette.success.main, 0.05)
									: "background.paper",
							},
						}}
					/>
				</Grid>

				{/* 3. Visitor Email */}
				<Grid item xs={12} sm={isExisting ? 6 : 4}>
					<TextField
						fullWidth
						size="small"
						label="Visitor Email"
						type="email"
						required={!isExisting}
						placeholder="e.g. visitor@yopmail.com"
						{...register("visitor_email", {
							required: !isExisting ? "Visitor email is required" : false,
							pattern: {
								value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
								message: "Invalid email address",
							},
						})}
						error={Boolean(errors.visitor_email)}
						helperText={errors.visitor_email?.message}
						InputProps={{
							readOnly: isExisting,
							startAdornment: (
								<InputAdornment position="start">
									<EmailIcon
										fontSize="small"
										color={isExisting ? "success" : "action"}
									/>
								</InputAdornment>
							),
						}}
						sx={{
							"& .MuiOutlinedInput-root": {
								bgcolor: isExisting
									? alpha(theme.palette.success.main, 0.05)
									: "background.paper",
							},
						}}
					/>
				</Grid>

				{/* 4. Password Field (Only needed for new visitor registration, default 12345678) */}
				{!isExisting && (
					<Grid item xs={12} sm={4}>
						<TextField
							fullWidth
							size="small"
							type={showPassword ? "text" : "password"}
							label="Account Password"
							placeholder="Default: 12345678"
							defaultValue="12345678"
							{...register("password")}
							helperText="Default: 12345678 (1-8)"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LockIcon fontSize="small" color="action" />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={() => setShowPassword((prev) => !prev)}
											edge="end"
											size="small"
										>
											{showPassword ? (
												<VisibilityOffIcon fontSize="small" />
											) : (
												<VisibilityIcon fontSize="small" />
											)}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Grid>
				)}
			</Grid>

			{/* Visitor Entry Pass Modal for Preview, Print & Download */}
			<VisitorPassModal
				open={passModalOpen}
				onClose={() => setPassModalOpen(false)}
				visitor={selectedVisitor}
			/>
		</Paper>
	);
};

export default VisitorPhoneSearchSection;
